import { useCallback, useState } from 'react';
import type { MapLocation } from '@repo/shared';
import { reverseGeocode } from '../geocode/hooks';

/** Fresh enough for a weather map; avoids waking GPS for a recent fix. */
const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 60_000,
};

export type GeolocationStatus = 'idle' | 'locating' | 'ready' | 'error';

export interface GeolocationState {
  status: GeolocationStatus;
  error?: string;
}

/**
 * Wraps the browser's geolocation API as a one-shot request.
 *
 * The raw fix is only a coordinate pair, so it's reverse-geocoded into a place
 * name before being handed back. That lookup is best-effort — if Mapbox isn't
 * configured or the call fails, the location still resolves, just labelled
 * generically. Losing the label shouldn't cost the user their location.
 */
export function useGeolocation(onLocated: (location: MapLocation) => void) {
  const [state, setState] = useState<GeolocationState>({ status: 'idle' });

  const request = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setState({ status: 'error', error: 'This browser has no location support.' });
      return;
    }

    setState({ status: 'locating' });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { longitude, latitude } = position.coords;

        const place = await reverseGeocode(longitude, latitude).catch(() => null);

        onLocated({
          label: place?.label ?? 'Current location',
          longitude,
          latitude,
        });
        setState({ status: 'ready' });
      },
      (error) => setState({ status: 'error', error: describe(error) }),
      GEOLOCATION_OPTIONS,
    );
  }, [onLocated]);

  return { ...state, request };
}

function describe(error: GeolocationPositionError): string {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Location permission denied. Search for an address instead.';
    case error.POSITION_UNAVAILABLE:
      return 'Your location is unavailable right now.';
    case error.TIMEOUT:
      return 'Timed out finding your location.';
    default:
      return 'Could not get your location.';
  }
}
